import { useEffect, useState } from 'react'
import Link from 'next/link'
import styled, { useTheme } from 'styled-components'
import { ProductCategoryScheme, SupplyTypeEnum } from '@/apis/rest/generated/types'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { Typo, TypoVariant } from '@/components/common/typography'
import { IconNameEnum } from '@/constants/icon-name.enum'
import ROUTES from '@/constants/legacy/constRoutes'
import { ProductDeliveryTypeEnum } from '@/constants/product-delivery-type.enum'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { useProductDetailEventTracker } from '@/containers/event-tracker/product-detail.event-tracker-new'
import { AdPetType, ProductSupplyType } from '@/graphql/generated/schemas'
import { localPetType } from '@/utils/utilLocalStorage'

interface ProductDetailShippingProps {
  shippingGuide: string
  shippingNotice: string
  productId: number
  productName: string
  categories: ProductCategoryScheme[]
  price: number
  salePrice: number
  supplyType: SupplyTypeEnum
  bundledProductExists: boolean
}

export const ProductDetailShipping = ({
  shippingGuide,
  shippingNotice,
  productId,
  productName,
  categories,
  price,
  salePrice,
  supplyType,
  bundledProductExists,
}: ProductDetailShippingProps) => {
  const { color } = useTheme()
  const { trackClickProductDetailBundleShippingButton } = useProductDetailEventTracker()
  const [petType, setPetType] = useState(AdPetType.Dog)

  useEffect(() => {
    const getLocalPetType = localPetType.load()
    if (getLocalPetType) {
      setPetType(getLocalPetType)
    }
  }, [])

  const shippingObject = [
    {
      id: 'shippingGuide',
      title: '배송 안내',
      description: shippingGuide,
      link: bundledProductExists,
      linkText: '묶음배송 상품 보기',
    },
    {
      id: 'shippingNotice',
      title: '배송 공지',
      description: shippingNotice,
      link: false,
      linkText: '',
    },
  ]

  const handleClickLink = () => {
    trackClickProductDetailBundleShippingButton({
      productId,
      productName,
      categories,
      price,
      salePrice,
      supplyType,
    })
  }

  return (
    <ProductDetailShippingWrapper className="product-detail-shipping">
      <ul className="product-detail-shipping__list">
        {shippingObject.map((item) => {
          return (
            <li className="product-detail-shipping__item" key={item.id}>
              <div className="product-detail-shipping__title">
                <Typo variant={TypoVariant.Body4Regular} color={color.gray400}>
                  {item.title}
                </Typo>
              </div>
              <div className="product-detail-shipping__content">
                <div className="product-detail-shipping__description">
                  <Typo
                    variant={TypoVariant.Body4Regular}
                    color={color.gray700}
                    style={{
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {item.description}
                  </Typo>
                </div>
                {item.link && (
                  <Link
                    href={{
                      pathname: ROUTES.PRODUCTS.BUNDLED_LIST,
                      query: {
                        productId,
                        type:
                          supplyType === ProductSupplyType.Consignment
                            ? ProductDeliveryTypeEnum.Others
                            : ProductDeliveryTypeEnum.Fitpet,
                        [QueryStringKeyEnum.SearchPetType]: petType,
                      },
                    }}
                  >
                    <button type="button" className="product-detail-shipping__link" onClick={handleClickLink}>
                      <Typo variant={TypoVariant.Body3Medium} color={color.gray900}>
                        {item.linkText}
                      </Typo>
                      <FlatIcon type={IconNameEnum.IcChevronRight} size="1.6rem" color={color.gray900} />
                    </button>
                  </Link>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </ProductDetailShippingWrapper>
  )
}

const ProductDetailShippingWrapper = styled.div`
  .product-detail-shipping {
    &__list {
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 2rem;
    }
    &__item {
      display: flex;
      flex-direction: row;
      gap: 2.4rem;
    }
    &__title {
      flex-shrink: 0;
    }
    &__link {
      cursor: pointer;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.4rem;
      padding: 0.6rem 1.2rem 0.6rem 1.2rem;
      margin-top: 0.8rem;
      border: 0.1rem solid
        ${({
          theme: {
            color: { gray200 },
          },
        }) => gray200};
      border-radius: 0.8rem;
      background-color: ${({
        theme: {
          color: { grayWhite },
        },
      }) => grayWhite};
    }
  }
`
